import {
	useRef,
	useEffect,
	RefObject,
	ChangeEvent,
	FormEvent,
	KeyboardEvent,
	ClipboardEvent,
	MouseEvent,
} from "react";
import { ArrowUp, Plus, Square } from "lucide-react";
import { UploadedFileCard } from "./File/UploadedFileCard";
import { ModeSelector } from "./ModeSelector";
import type { FileWithUpload } from "../types/FileWithUpload";
import type { Mode, ModeId } from "../types/Mode";

interface ChatInputFormProps {
	query: string;
	modeId: ModeId;
	modes: Mode[];
	uploadedFiles: FileWithUpload[];
	loading: boolean;
	hasUploadingFiles: boolean;
	contentEditableRef?: RefObject<HTMLDivElement>;
	bottom?: boolean;
	onQueryChange: (query: string) => void;
	onModeIdChange: (modeId: ModeId) => void;
	onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
	onRemoveFile: (file: File) => void;
	onSubmit: (e: FormEvent) => void;
	onStop: () => void;
}

export function ChatInputForm({
	query,
	modeId,
	modes,
	uploadedFiles,
	loading,
	hasUploadingFiles,
	contentEditableRef,
	bottom = false,
	onQueryChange,
	onModeIdChange,
	onFileChange,
	onRemoveFile,
	onSubmit,
	onStop,
}: ChatInputFormProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const isUpdatingFromStateRef = useRef(false);

	// Sync contentEditable with query state (especially for clearing after submit)
	useEffect(() => {
		if (contentEditableRef?.current && contentEditableRef.current.textContent !== query) {
			isUpdatingFromStateRef.current = true;
			contentEditableRef.current.textContent = query;
			// Reset flag after a microtask to allow input event to process
			Promise.resolve().then(() => {
				isUpdatingFromStateRef.current = false;
			});
		}
	}, [query, contentEditableRef]);

	const handleInput = (e: FormEvent<HTMLDivElement>) => {
		// Skip if update is coming from state sync (to avoid loops)
		if (isUpdatingFromStateRef.current) {
			return;
		}
		const text = e.currentTarget.textContent || "";
		onQueryChange(text);
	};

	const handleRemoveFile = (file: File) => {
		onRemoveFile(file);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			if (loading) {
				onStop();
				return;
			}
			if (!hasUploadingFiles && (modeId !== "agent" || query.trim())) {
				onSubmit(e as unknown as FormEvent);
			}
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text/plain");

		// Use insertText for better undo/redo support and caret handling
		if (document.queryCommandSupported("insertText")) {
			document.execCommand("insertText", false, text);
		} else {
			const selection = window.getSelection();
			if (!selection?.rangeCount) return;
			selection.deleteFromDocument();
			selection.getRangeAt(0).insertNode(document.createTextNode(text));
			selection.collapseToEnd();
		}
	};

	const handleContainerClick = (e: MouseEvent) => {
		const target = e.target as HTMLElement;
		if (!target.closest('button, a, [role="button"], input, [data-no-focus]')) {
			contentEditableRef?.current?.focus();
		}
	};

	const isDisabled = (!loading && modeId === "agent" && !query) || hasUploadingFiles;

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				if (isDisabled) {
					return;
				}
				if (!loading) {
					onSubmit(e);
				} else {
					onStop();
				}
			}}
			className="w-full mx-auto"
		>
			<div
				onClick={handleContainerClick}
				className="relative flex flex-col p-1 bg-white border border-base-300 rounded-xl shadow-lg overflow-visible cursor-text"
			>
				<div className="flex flex-col p-0.5">
					{/* Fichiers uploadés */}
					{uploadedFiles.length > 0 && (
						<div className="flex flex-wrap gap-2 mb-2 px-1">
							{uploadedFiles.map((fileWithUpload, index) => (
								<UploadedFileCard
									key={`${fileWithUpload.file.name}-${index}`}
									file={fileWithUpload.file}
									onRemove={() => handleRemoveFile(fileWithUpload.file)}
									disabled={loading}
									isUploading={fileWithUpload.isUploading}
									uploadError={fileWithUpload.uploadError}
								/>
							))}
						</div>
					)}

					<div className="grid min-w-0 p-1">
						<div className="[grid-area:1/1] min-w-0">
							<div className="max-h-[25vh] min-h-10 w-full overflow-y-auto text-sm break-words">
								<div
									ref={contentEditableRef}
									contentEditable
									role="textbox"
									aria-label="Écrivez votre demande."
									aria-multiline={true}
									aria-required={false}
									aria-invalid={false}
									translate="no"
									className={`outline-none min-h-[2.5rem] ${!query ? "is-empty is-editor-empty" : ""}`}
									onInput={handleInput}
									onKeyDown={handleKeyDown}
									onPaste={handlePaste}
									data-placeholder="Posez votre question..."
									suppressContentEditableWarning
								/>
							</div>
						</div>
					</div>

					<div className="flex w-full items-center justify-between gap-2 px-1 pb-0.5">
						<div className="flex items-center gap-2 min-w-0 flex-1">
							<ModeSelector
								modes={modes}
								value={modeId}
								onChange={onModeIdChange}
								disabled={loading}
								isBottom={bottom}
							/>
							<input
								ref={fileInputRef}
								type="file"
								accept=".pdf,.doc,.docx"
								onChange={onFileChange}
								multiple
								disabled={loading}
								className="hidden"
							/>
							<div
								className={`tooltip tooltip-secondary ${bottom ? "tooltip-top" : "tooltip-bottom"
									} z-10`}
								data-tip="Importer des documents"
							>
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									disabled={loading}
									className="btn btn-square btn-ghost size-8 text-base-content/70 hover:bg-base-200 hover:border-base-200 text-sm"
									aria-label="Télécharger un document"
								>
									<Plus className="size-5" />
								</button>
							</div>
						</div>
						<button
							type="submit"
							disabled={isDisabled}
							className="btn btn-square btn-secondary size-8 transition-colors"
						>
							{loading ? (
								<Square className="size-3 text-white fill-white" />
							) : (
								<ArrowUp className="size-5" />
							)}
						</button>
					</div>
				</div>
			</div>
		</form>
	);
}
